import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { type PlayUpdate, competitionRanks, formatOrdinal, tiedRanks } from '@topmeeps/shared'
import { useEffect, useState } from 'react'
import { usePatchPlay, usePlay } from '../api/hooks'
import { ScreenShell } from '../components/ScreenShell'
import { BackButton, GameThumb, MeepleAvatar, NumberCircle, TopBar } from '../primitives'

type EditableParticipant = {
  id: string
  userId: string | null
  guestId: string | null
  displayName: string
  avatarHue: number
  avatarShape: 'meeple' | 'circle'
  rank: number
  tiedWithPrevious: boolean
  score: number | null
  subscores: Record<string, number>
  outcomeTags: string[]
}

// Re-derive each participant's `rank` from the array order (canonical
// finishing order) + tie flags, so the stored rank is always a valid
// standard-competition ranking (1,1,3 …).
const resyncRanks = (list: EditableParticipant[]): EditableParticipant[] => {
  const ranks = competitionRanks(list.map((p, i) => i > 0 && p.tiedWithPrevious))
  return list.map((p, i) => ({
    ...p,
    tiedWithPrevious: i > 0 && p.tiedWithPrevious,
    rank: ranks[i] ?? i + 1,
  }))
}

const toLocalDateTimeInput = (iso: string): string => {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const PlayEditScreen = () => {
  const { id } = useParams({ from: '/play/$id/edit' })
  const navigate = useNavigate()
  const play = usePlay(id)
  const patch = usePatchPlay(id, play.data?.play.groupId ?? null)

  const [participants, setParticipants] = useState<EditableParticipant[]>([])
  const [playedAt, setPlayedAt] = useState('')
  const [durationMinutes, setDurationMinutes] = useState<number | null>(null)
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [hydrated, setHydrated] = useState(false)

  // Pre-populate state when the play loads.
  useEffect(() => {
    if (!play.data || hydrated) return
    const sorted = play.data.participants.slice().sort((a, b) => a.rank - b.rank)
    setParticipants(
      sorted.map((p, i) => ({
        id: p.id,
        userId: p.userId,
        guestId: p.guestId,
        displayName: p.displayName ?? '(guest)',
        avatarHue: p.avatarHue ?? 0,
        avatarShape: p.avatarShape ?? 'meeple',
        rank: p.rank,
        // Reconstruct tie groups: a player is tied with the one above when
        // they share the same stored rank.
        tiedWithPrevious: i > 0 && p.rank === sorted[i - 1]!.rank,
        score: p.score,
        subscores: p.subscores ?? {},
        outcomeTags: p.outcomeTags ?? [],
      })),
    )
    setPlayedAt(play.data.play.playedAt)
    setDurationMinutes(play.data.play.durationMinutes)
    setLocation(play.data.play.location ?? '')
    setNotes(play.data.play.notes ?? '')
    setHydrated(true)
  }, [play.data, hydrated])

  const schema = play.data?.play.scoreSchemaSnapshot ?? play.data?.game.scoreSchema

  // Array order is the canonical finishing order; rank/tiedWithPrevious stay
  // in sync via resyncRanks.
  const ordered = participants
  const tiedPlaces = tiedRanks(ordered.map((p) => p.rank))

  const move = (id: string, dir: -1 | 1) => {
    setParticipants((prev) => {
      const idx = prev.findIndex((p) => p.id === id)
      const swapWith = idx + dir
      if (idx < 0 || swapWith < 0 || swapWith >= prev.length) return prev
      const next = [...prev]
      const a = next[idx]!
      const b = next[swapWith]!
      // Reordering separates these two players; drop any tie across the
      // boundary they crossed and let the rest re-derive.
      next[idx] = { ...b, tiedWithPrevious: false }
      next[swapWith] = { ...a, tiedWithPrevious: false }
      return resyncRanks(next)
    })
  }

  const toggleTie = (id: string) => {
    setParticipants((prev) =>
      resyncRanks(
        prev.map((p) => (p.id === id ? { ...p, tiedWithPrevious: !p.tiedWithPrevious } : p)),
      ),
    )
  }

  const setScore = (id: string, score: number | null) => {
    setParticipants((prev) => prev.map((p) => (p.id === id ? { ...p, score } : p)))
  }

  const setSubscore = (id: string, label: string, value: number) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === id ? { ...p, subscores: { ...p.subscores, [label]: value } } : p)),
    )
  }

  const onSave = async () => {
    if (!play.data) return
    const payload: PlayUpdate = {
      playedAt,
      durationMinutes,
      location: location || null,
      notes: notes || null,
      expansions: null,
      participants: ordered.map((p) => ({
        userId: p.userId,
        guestId: p.guestId,
        rank: p.rank,
        score: p.score,
        outcomeTags: p.outcomeTags,
        subscores: p.subscores,
      })),
    }
    await patch.mutateAsync(payload)
    navigate({ to: '/play/$id', params: { id } })
  }

  if (play.isLoading) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>
      </ScreenShell>
    )
  }
  if (!play.data) return null
  if (play.data.editAuthority === 'none') {
    return (
      <ScreenShell>
        <TopBar
          left={<BackButton fallback={() => navigate({ to: '/play/$id', params: { id } })} />}
          title="Edit play"
        />
        <div style={{ padding: 16 }}>
          <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 18 }}>
              Edit window closed.
            </div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Plays can be edited within 24 hours by participants, or 7 days by group admins.
            </div>
          </div>
        </div>
      </ScreenShell>
    )
  }

  return (
    <ScreenShell>
      <TopBar
        left={<BackButton fallback={() => navigate({ to: '/play/$id', params: { id } })} />}
        title="Edit play"
        sub={`Recompute on save — ${ordered.length} player${ordered.length === 1 ? '' : 's'}`}
      />

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          className="felt-card"
          style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}
        >
          <GameThumb label={play.data.game.name} imageUrl={play.data.game.imageUrl} size={42} />
          <div style={{ flex: 1 }}>
            <div className="kicker">game (not editable)</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>
              {play.data.game.name}
            </div>
          </div>
        </div>

        <div>
          <div className="kicker" style={{ padding: '0 4px 6px' }}>
            results
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ordered.map((p, i) => (
              <div
                key={p.id}
                className="felt-card"
                style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {i > 0 && (
                  <button
                    type="button"
                    className={p.tiedWithPrevious ? 'btn-stamp' : 'btn-ghost'}
                    onClick={() => toggleTie(p.id)}
                    style={{
                      alignSelf: 'center',
                      padding: '1px 10px',
                      fontSize: 10,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}
                    aria-pressed={p.tiedWithPrevious}
                  >
                    {p.tiedWithPrevious ? '= tied with above' : '= tie with above'}
                  </button>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <NumberCircle n={p.rank} winner={p.rank === 1} size={26} />
                  <MeepleAvatar
                    name={p.displayName}
                    hue={p.avatarHue}
                    shape={p.avatarShape}
                    size={32}
                    ghost={p.guestId !== null}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{p.displayName}</span>
                    {tiedPlaces.has(p.rank) && (
                      <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        Tied for {formatOrdinal(p.rank)}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => move(p.id, -1)}
                    disabled={i === 0}
                    style={{ padding: '4px 8px' }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => move(p.id, 1)}
                    disabled={i === ordered.length - 1}
                    style={{ padding: '4px 8px' }}
                  >
                    ↓
                  </button>
                </div>

                {schema?.scoreField.enabled && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: 'var(--ink-2)',
                    }}
                  >
                    <span style={{ minWidth: 90 }}>{schema.scoreField.label}</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={p.score ?? ''}
                      onChange={(e) => {
                        const v = e.currentTarget.value
                        setScore(p.id, v === '' ? null : Number(v))
                      }}
                      style={{ width: 90 }}
                    />
                  </label>
                )}

                {schema?.subscores.map((sub) => (
                  <label
                    key={sub.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: 'var(--ink-2)',
                    }}
                  >
                    <span style={{ minWidth: 90 }}>{sub.label}</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={p.subscores[sub.label] ?? ''}
                      onChange={(e) => {
                        const v = e.currentTarget.value
                        if (v === '') return
                        setSubscore(p.id, sub.label, Number(v))
                      }}
                      style={{ width: 90 }}
                    />
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6, paddingLeft: 4 }}>
            To add or remove a player, delete this play and re-record.
          </div>
        </div>

        <div>
          <div className="kicker" style={{ padding: '0 4px 6px' }}>
            details
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="kicker">When</span>
              <input
                type="datetime-local"
                value={toLocalDateTimeInput(playedAt)}
                onChange={(e) => setPlayedAt(new Date(e.currentTarget.value).toISOString())}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="kicker">Duration (minutes)</span>
              <input
                type="number"
                inputMode="numeric"
                value={durationMinutes ?? ''}
                onChange={(e) => {
                  const v = e.currentTarget.value
                  setDurationMinutes(v === '' ? null : Number(v))
                }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="kicker">Location</span>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.currentTarget.value)}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="kicker">Notes</span>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/play/$id" params={{ id }} style={{ flex: 1 }}>
            <button type="button" className="btn-ghost" style={{ width: '100%' }}>
              Cancel
            </button>
          </Link>
          <button
            type="button"
            className="btn-stamp"
            style={{ flex: 1 }}
            onClick={onSave}
            disabled={patch.isPending}
          >
            {patch.isPending ? 'Recomputing…' : 'Save changes'}
          </button>
        </div>
        {patch.isError && (
          <div style={{ color: 'var(--terra-ink)', fontSize: 12 }}>
            {(patch.error as Error).message}
          </div>
        )}
      </div>
    </ScreenShell>
  )
}
